import { Axios } from 'axios'
import { APIError } from './_axios'
import { EmailTemplate, EmailCampaign, EmailSettings, CampaignStats, CampaignFilters, PaginationParams } from './_models_email'

// Email Templates

export function createEmailTemplate(axios: Axios, data: {
  name: string;
  prompt_template: string;
  subject_template: string;
  account_id?: string;
  is_global?: boolean;
}): Promise<EmailTemplate> {
  return axios.post(`/authenticated/admin/email-templates`, data)
    .then((res) => res.data)
    .then((response: any) => {
      if (!response.data.template) {
        throw new APIError('Malformed response', {})
      }
      return response.data.template
    })
}

export function getEmailTemplates(axios: Axios, accountId?: string): Promise<EmailTemplate[]> {
  const params = accountId ? { account_id: accountId } : {}
  return axios.get(`/authenticated/admin/email-templates`, { params })
    .then((res) => res.data)
    .then((response: any) => {
      if (!response.data.templates) {
        throw new APIError('Malformed response', {})
      }
      return response.data.templates
    })
}

export function updateEmailTemplate(axios: Axios, templateId: string, data: Partial<EmailTemplate>): Promise<EmailTemplate> {
  return axios.put(`/authenticated/admin/email-templates/${templateId}`, data)
    .then((res) => res.data)
    .then((response: any) => {
      if (!response.data.template) {
        throw new APIError('Malformed response', {})
      }
      return response.data.template
    })
}

export function deleteEmailTemplate(axios: Axios, templateId: string): Promise<void> {
  return axios.delete(`/authenticated/admin/email-templates/${templateId}`)
    .then((res) => res.data)
    .then((response: any) => {
      if (!response.success) {
        throw new APIError('Failed to delete template', {})
      }
    })
}

// Email Settings

export function getEmailSettings(axios: Axios, accountUniqueId: string): Promise<EmailSettings> {
  return axios.get(`/authenticated/admin/accounts/${accountUniqueId}/email-settings`)
    .then((res) => res.data)
    .then((response: any) => {
      if (!response.data.email_settings) {
        throw new APIError('Malformed response', {})
      }
      return response.data.email_settings
    })
}

export function updateEmailSettings(axios: Axios, accountUniqueId: string, settings: EmailSettings): Promise<EmailSettings> {
  return axios.post(`/authenticated/admin/accounts/${accountUniqueId}/email-settings`, {
    email_settings: settings
  })
    .then((res) => res.data)
    .then((response: any) => {
      if (!response.data.email_settings) {
        throw new APIError('Malformed response', {})
      }
      return response.data.email_settings
    })
}

// Email Campaigns

export function getEmailCampaigns(axios: Axios, filters?: CampaignFilters & PaginationParams): Promise<{
  campaigns: EmailCampaign[];
  pagination: PaginationParams;
}> {
  return axios.get(`/authenticated/current-account/email-campaigns`, { params: filters })
    .then((res) => res.data)
    .then((response: any) => {
      if (!response.campaigns) {
        throw new APIError('Malformed response', {})
      }
      return {
        campaigns: response.campaigns,
        pagination: response.pagination
      }
    })
}

export function getPendingEmailCampaigns(axios: Axios, params?: {
  account_id?: string;
  limit?: number;
  offset?: number;
  all_accounts?: boolean;
}): Promise<EmailCampaign[]> {
  const requestParams = {
    ...params,
    all_accounts: params?.all_accounts ? 'true' : undefined
  };
  return axios.get(`/authenticated/current-account/email-campaigns/pending`, { params: requestParams })
    .then((res) => res.data)
    .then((response: any) => {
      // The backend returns {success: true, campaigns: [...]}
      if (response.campaigns !== undefined) {
        return response.campaigns;
      }
      throw new APIError('Malformed response', {})
    })
}

export function getFailedEmailCampaigns(axios: Axios, params?: {
  account_id?: string;
  limit?: number;
  offset?: number;
}): Promise<EmailCampaign[]> {
  return axios.get(`/authenticated/current-account/email-campaigns/failed`, { params })
    .then((res) => res.data)
    .then((response: any) => {
      if (!response.campaigns) {
        throw new APIError('Malformed response', {})
      }
      return response.campaigns
    })
}

export function getHistoricalEmailCampaigns(axios: Axios, params?: {
  account_id?: string;
  limit?: number;
  offset?: number;
  all_accounts?: boolean;
}): Promise<EmailCampaign[]> {
  const requestParams = {
    ...params,
    all_accounts: params?.all_accounts ? 'true' : undefined
  };
  return axios.get(`/authenticated/current-account/email-campaigns/history`, { params: requestParams })
    .then((res) => res.data)
    .then((response: any) => {
      console.log('Raw response from /history endpoint:', response);
      
      // The backend returns {success: true, campaigns: [...]}
      if (response.campaigns !== undefined) {
        return response.campaigns;
      }
      
      console.error('Malformed response - no campaigns field:', response);
      throw new APIError('Malformed response', {})
    })
}

export function approveEmailCampaign(axios: Axios, campaignId: string, updatedContent?: string | { email_content: string; email_subject: string }): Promise<EmailCampaign> {
  return axios.post(`/authenticated/current-account/email-campaigns/${campaignId}/approve`, {
    updated_content: updatedContent
  })
    .then((res) => res.data)
    .then((response: any) => {
      if (!response.data.campaign) {
        throw new APIError('Malformed response', {})
      }
      return response.data.campaign
    })
}

export function rejectEmailCampaign(axios: Axios, campaignId: string, reason?: string): Promise<EmailCampaign> {
  return axios.post(`/authenticated/current-account/email-campaigns/${campaignId}/reject`, {
    reason: reason || 'Rejected by admin'
  })
    .then((res) => res.data)
    .then((response: any) => {
      if (!response.data.campaign) {
        throw new APIError('Malformed response', {})
      }
      return response.data.campaign
    })
}

export function retryEmailCampaign(axios: Axios, campaignId: string): Promise<void> {
  return axios.post(`/authenticated/current-account/email-campaigns/${campaignId}/retry`)
    .then((res) => res.data)
    .then((response: any) => {
      if (!response.success) {
        throw new APIError('Failed to retry campaign', {})
      }
    })
}

export function batchApproveEmailCampaigns(axios: Axios, campaignIds: string[]): Promise<{
  succeeded: number;
  failed: number;
  errors: Record<string, string>;
}> {
  return axios.post(`/authenticated/current-account/email-campaigns/batch-approve`, {
    campaign_ids: campaignIds
  })
    .then((res) => res.data)
    .then((response: any) => {
      if (!response.data.result) {
        throw new APIError('Malformed response', {})
      }
      return response.data.result
    })
}

export function batchRetryEmailCampaigns(axios: Axios, campaignIds: string[]): Promise<{
  succeeded: number;
  failed: number;
  errors: Record<string, string>;
}> {
  return axios.post(`/authenticated/current-account/email-campaigns/batch-retry`, {
    campaign_ids: campaignIds
  })
    .then((res) => res.data)
    .then((response: any) => {
      if (!response.data.result) {
        throw new APIError('Malformed response', {})
      }
      return response.data.result
    })
}

export function getEmailCampaignStats(axios: Axios, accountId?: string | null, dateRange?: string, allAccounts?: boolean): Promise<CampaignStats> {
  const params: any = {}
  if (accountId) params.account_id = accountId
  if (dateRange) params.date_range = dateRange
  if (allAccounts) params.all_accounts = 'true'
  
  return axios.get(`/authenticated/current-account/email-campaigns/stats`, { params })
    .then((res) => res.data)
    .then((response: any) => {
      if (!response.data.stats) {
        throw new APIError('Malformed response', {})
      }
      return response.data.stats
    })
}